import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";
import { MainLayout } from "./components/layout/MainLayout";
import { Toaster } from "./components/ui/sonner";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import DashboardPage from "./pages/DashboardPage";
import LeasingClientsPage from "./pages/LeasingClientsPage";
import LeasingDashboardPage from "./pages/LeasingDashboardPage";
import LeasingManagersPage from "./pages/LeasingManagersPage";
import LeasingMaintenancePage from "./pages/LeasingMaintenancePage";
import LeasingWorkshopsPage from "./pages/LeasingWorkshopsPage";
import LeasingChatPage from "./pages/LeasingChatPage";
import WorkshopJobsPage from "./pages/WorkshopJobsPage";
import WorkshopAppointmentsPage from "./pages/WorkshopAppointmentsPage";
import WorkshopCustomersPage from "./pages/WorkshopCustomersPage";
import WorkshopPartnersPage from "./pages/WorkshopPartnersPage";
import WorkshopSettingsPage from "./pages/WorkshopSettingsPage";
import LeasingVehiclesPage from "./pages/LeasingVehiclesPage";
import { useState, useEffect } from "react";
import api from "./lib/api";
import DriverDashboardPage from "./pages/DriverDashboardPage";
import SuperAdminDashboard from "./pages/SuperAdminDashboard";
import CompanyManagement from "./pages/CompanyManagement";
import UserManagement from "./pages/UserManagement";
import VehiclesPage from "./pages/VehiclesPage";
import VehicleDetailsPage from "./pages/VehicleDetailsPage";
import DriversPage from "./pages/DriversPage";
import MaintenancePage from "./pages/MaintenancePage";
import FuelLogsPage from "./pages/FuelLogsPage";
import InspectionsPage from "./pages/InspectionsPage";
import ComparisonPage from "./pages/ComparisonPage";
import DriverCheckInPage from "./pages/DriverCheckInPage";
import HistoryPage from "./pages/HistoryPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import DamageReportsPage from "./pages/DamageReportsPage";
import UsersPage from "./pages/UsersPage";
import SettingsPage from "./pages/SettingsPage";
import DemoPage from "./pages/DemoPage";
import BillingPage from "./pages/BillingPage";
import FleetOptimizationPage from "./pages/FleetOptimizationPage";
import CapacityAnalysisPage from "./pages/CapacityAnalysisPage";
import LiveFleetPage from "./pages/LiveFleetPage";
import ParkingMapPage from "./pages/ParkingMapPage";
import { Loader2 } from "lucide-react";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <Loader2 className="w-8 h-8 text-[#1B7EB5] animate-spin" />
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    if (allowedRoles && !allowedRoles.includes(user.role)) {
        return <Navigate to="/dashboard" replace />;
    }

    return children;
};

// Auth Route (redirect if already logged in)
const AuthRoute = ({ children }) => {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <Loader2 className="w-8 h-8 text-[#1B7EB5] animate-spin" />
            </div>
        );
    }

    if (user) {
        return <Navigate to="/app/dashboard" replace />;
    }

    return children;
};

// Landing Page Route (show landing if not logged in, redirect to app if logged in)
const LandingPageRoute = () => {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <Loader2 className="w-8 h-8 text-[#1B7EB5] animate-spin" />
            </div>
        );
    }

    if (user) {
        return <Navigate to="/app/dashboard" replace />;
    }

    return <LandingPage />;
};

// Smart Dashboard - shows different dashboard based on user role
const SmartDashboard = () => {
    const { user } = useAuth();
    
    if (user?.role === 'super_admin') {
        return <SuperAdminDashboard />;
    }
    
    if (user?.role === 'driver') {
        return <DriverDashboardPage />;
    }

    if (user?.role === 'workshop') {
        return <Navigate to="/app/workshop-jobs" replace />;
    }
    
    return <CompanyDashboard />;
};

const CompanyDashboard = () => {
    const [edition, setEdition] = useState(null);
    useEffect(() => {
        api.get('/auth/company').then((res) => setEdition(res.data.edition || 'carrier')).catch(() => setEdition('carrier'));
    }, []);
    if (!edition) return null;
    if (edition === 'leasing') return <LeasingDashboardPage />;
    if (edition === 'workshop') return <Navigate to="/app/workshop-jobs" replace />;
    return <DashboardPage />;
};

function AppRoutes() {
    return (
        <Routes>
            {/* Public Landing Page */}
            <Route path="/" element={<LandingPageRoute />} />
            
            {/* Public Demo Page */}
            <Route path="/demo" element={<DemoPage />} />
            
            {/* Auth Routes */}
            <Route path="/login" element={
                <AuthRoute>
                    <LoginPage />
                </AuthRoute>
            } />
            <Route path="/register" element={
                <AuthRoute>
                    <RegisterPage />
                </AuthRoute>
            } />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />

            {/* Protected Routes */}
            <Route path="/app" element={
                <ProtectedRoute>
                    <MainLayout />
                </ProtectedRoute>
            }>
                <Route index element={<Navigate to="/app/dashboard" replace />} />
                <Route path="dashboard" element={<SmartDashboard />} />
                
                {/* Super Admin Routes */}
                <Route path="super-admin/companies" element={
                    <ProtectedRoute allowedRoles={['super_admin']}>
                        <CompanyManagement />
                    </ProtectedRoute>
                } />
                <Route path="super-admin/companies/:id" element={
                    <ProtectedRoute allowedRoles={['super_admin']}>
                        <CompanyManagement />
                    </ProtectedRoute>
                } />
                <Route path="super-admin/users" element={
                    <ProtectedRoute allowedRoles={['super_admin']}>
                        <UserManagement />
                    </ProtectedRoute>
                } />
                
                <Route path="vehicles" element={<VehiclesPage />} />
                <Route path="vehicles/:id" element={
                    <ProtectedRoute allowedRoles={['admin', 'fleet_manager', 'super_admin']}>
                        <VehicleDetailsPage />
                    </ProtectedRoute>
                } />
                <Route path="drivers" element={
                    <ProtectedRoute allowedRoles={['admin', 'fleet_manager', 'super_admin']}>
                        <DriversPage />
                    </ProtectedRoute>
                } />
                <Route path="maintenance" element={<MaintenancePage />} />
                <Route path="fuel-logs" element={<FuelLogsPage />} />
                <Route path="inspections" element={
                    <ProtectedRoute allowedRoles={['admin', 'fleet_manager', 'super_admin']}>
                        <InspectionsPage />
                    </ProtectedRoute>
                } />
                <Route path="comparison" element={
                    <ProtectedRoute allowedRoles={['admin', 'fleet_manager', 'super_admin']}>
                        <ComparisonPage />
                    </ProtectedRoute>
                } />
                <Route path="history" element={
                    <ProtectedRoute allowedRoles={['admin', 'fleet_manager', 'super_admin']}>
                        <HistoryPage />
                    </ProtectedRoute>
                } />
                <Route path="analytics" element={
                    <ProtectedRoute allowedRoles={['admin', 'fleet_manager', 'super_admin']}>
                        <AnalyticsPage />
                    </ProtectedRoute>
                } />
                <Route path="damage-reports" element={
                    <ProtectedRoute allowedRoles={['admin', 'fleet_manager', 'super_admin']}>
                        <DamageReportsPage />
                    </ProtectedRoute>
                } />
                <Route path="check-in" element={<DriverCheckInPage />} />
                <Route path="users" element={
                    <ProtectedRoute allowedRoles={['admin']}>
                        <UsersPage />
                    </ProtectedRoute>
                } />
                <Route path="billing" element={
                    <ProtectedRoute allowedRoles={['admin', 'fleet_manager']}>
                        <BillingPage />
                    </ProtectedRoute>
                } />
                <Route path="optimization" element={
                    <ProtectedRoute allowedRoles={['admin', 'fleet_manager']}>
                        <FleetOptimizationPage />
                    </ProtectedRoute>
                } />
                <Route path="leasing-clients" element={
                    <ProtectedRoute allowedRoles={['admin', 'fleet_manager']}>
                        <LeasingClientsPage />
                    </ProtectedRoute>
                } />
                <Route path="leasing-chat" element={
                    <ProtectedRoute allowedRoles={['admin', 'fleet_manager', 'workshop']}>
                        <LeasingChatPage />
                    </ProtectedRoute>
                } />
                <Route path="workshop-jobs" element={
                    <ProtectedRoute allowedRoles={['workshop', 'admin', 'fleet_manager']}>
                        <WorkshopJobsPage />
                    </ProtectedRoute>
                } />
                <Route path="workshop-appointments" element={
                    <ProtectedRoute allowedRoles={['admin', 'fleet_manager']}>
                        <WorkshopAppointmentsPage />
                    </ProtectedRoute>
                } />
                <Route path="workshop-customers" element={
                    <ProtectedRoute allowedRoles={['admin', 'fleet_manager']}>
                        <WorkshopCustomersPage />
                    </ProtectedRoute>
                } />
                <Route path="workshop-partners" element={
                    <ProtectedRoute allowedRoles={['admin']}>
                        <WorkshopPartnersPage />
                    </ProtectedRoute>
                } />
                <Route path="workshop-settings" element={
                    <ProtectedRoute allowedRoles={['workshop', 'admin']}>
                        <WorkshopSettingsPage />
                    </ProtectedRoute>
                } />
                <Route path="leasing-workshops" element={
                    <ProtectedRoute allowedRoles={['admin', 'fleet_manager']}>
                        <LeasingWorkshopsPage />
                    </ProtectedRoute>
                } />
                <Route path="leasing-maintenance" element={
                    <ProtectedRoute allowedRoles={['admin', 'fleet_manager']}>
                        <LeasingMaintenancePage />
                    </ProtectedRoute>
                } />
                <Route path="leasing-managers" element={
                    <ProtectedRoute allowedRoles={['admin']}>
                        <LeasingManagersPage />
                    </ProtectedRoute>
                } />
                <Route path="leasing-vehicles" element={
                    <ProtectedRoute allowedRoles={['admin', 'fleet_manager']}>
                        <LeasingVehiclesPage />
                    </ProtectedRoute>
                } />
                <Route path="capacity-analysis" element={
                    <Navigate to="/app/dashboard" replace />
                } />
                <Route path="live-fleet" element={
                    <ProtectedRoute allowedRoles={['admin', 'fleet_manager']}>
                        <LiveFleetPage />
                    </ProtectedRoute>
                } />
                <Route path="parking-map" element={
                    <ProtectedRoute allowedRoles={['admin', 'fleet_manager', 'driver']}>
                        <ParkingMapPage />
                    </ProtectedRoute>
                } />
                <Route path="settings" element={<SettingsPage />} />
            </Route>

            {/* Legacy routes - redirect to /app */}
            <Route path="/dashboard" element={<Navigate to="/app/dashboard" replace />} />
            <Route path="/vehicles" element={<Navigate to="/app/vehicles" replace />} />
            <Route path="/drivers" element={<Navigate to="/app/drivers" replace />} />
            <Route path="/maintenance" element={<Navigate to="/app/maintenance" replace />} />
            <Route path="/fuel-logs" element={<Navigate to="/app/fuel-logs" replace />} />
            <Route path="/inspections" element={<Navigate to="/app/inspections" replace />} />
            <Route path="/comparison" element={<Navigate to="/app/comparison" replace />} />
            <Route path="/history" element={<Navigate to="/app/history" replace />} />
            <Route path="/analytics" element={<Navigate to="/app/analytics" replace />} />
            <Route path="/damage-reports" element={<Navigate to="/app/damage-reports" replace />} />
            <Route path="/check-in" element={<Navigate to="/app/check-in" replace />} />
            <Route path="/users" element={<Navigate to="/app/users" replace />} />
            <Route path="/settings" element={<Navigate to="/app/settings" replace />} />

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <LanguageProvider>
            <AuthProvider>
                <BrowserRouter>
                    <AppRoutes />
                </BrowserRouter>
                <Toaster position="bottom-right" />
            </AuthProvider>
        </LanguageProvider>
    );
}

export default App;
